/**
 * AI:
 * Status: 'confirmed'
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Logs tab — renders the LogicFlowTrace entries for the selected record
 *         via a QL-widget. Provides basic level filtering and a refresh action.
 */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import {WidgetData} from '../../../../../../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../../../../../../core/shared/misc/sorting';

interface LevelOption {
    label: string;
    value: string;
}

@Component({
    selector: 'ei-tooling-logs-tab',
    templateUrl: './ei-tooling-logs-tab.component.html',
    styleUrls: ['./ei-tooling-logs-tab.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EiToolingLogsTabComponent implements OnChanges {

    @Input() recordId: number | null = null;

    traceWidget: WidgetData | null = null;

    /** Simple level filter (display-only, widget doesn't filter on level). */
    levelOptions: LevelOption[] = [
        {label: 'Info', value: 'INFO'},
        {label: 'Warnung', value: 'WARN'},
        {label: 'Fehler', value: 'ERROR'},
    ];
    selectedLevels: string[] = ['INFO', 'WARN', 'ERROR'];

    constructor(private readonly cdr: ChangeDetectorRef) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['recordId']) {
            this.rebuildWidget();
        }
    }

    /**
     * Forces a rebuild of the widget so the widget host re-queries the backend.
     */
    refresh(): void {
        this.rebuildWidget();
    }

    /**
     * Builds the LogicFlowTrace QL widget filtered to this record.
     */
    private rebuildWidget(): void {
        if (this.recordId == null) {
            this.traceWidget = null;
            this.cdr.markForCheck();
            return;
        }

        const filters = [
            FilterCriteria.create(
                'e.callerObjectType.alias',
                FilterCriteria.cOperatorEqual,
                'ei.EiImportRecord'
            ),
            FilterCriteria.create(
                'e.callerObjectId',
                FilterCriteria.cOperatorEqual,
                this.recordId
            ),
        ];

        /**
         * AI: Status: confirmed
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Zeigt den LogicFlowTrace (lg.LogicFlowTrace) für den aktuell ausgewählten EI-Import-Datensatz
         *   im Tooling-Logs-Tab. Runtime-built, weil die QL-Filter auf callerObjectType='ei.EiImportRecord'
         *   und die dynamische recordId gebunden sind und bei jedem Record-Wechsel neu erzeugt werden müssen,
         *   um den Trace-Scope auf den jeweiligen Datensatz einzuschränken.
         */
        this.traceWidget = WidgetFactory.createWidgetTableEntityBasicQl(
            'ei.dashboard.tooling.logs.' + this.recordId,
            'Ausführungs-Trace',
            'lg.LogicFlowTrace',
            filters,
            [new Sorting('e.startedAt', true)],
            true,
        );
        this.cdr.markForCheck();
    }
}
